import type {
  DetectorRuleResponse,
  FixedParameterResponse,
  QueryParameterResponse,
  QueryPurpose,
  QuerySource,
  RuleUnit,
  ThresholdRuleName,
} from '@prerender/diagnostics-api';

import { severityLabel } from './severityLabels';

export const DETECTOR_QUERIES = 'Detector queries';

const PURPOSE_LABELS: Record<QueryPurpose, string> = {
  detection: 'Detection',
  enrichment: 'Enrichment',
};

const SOURCE_LABELS: Record<QuerySource, string> = {
  'clickhouse-domain-crawler-metrics': 'ClickHouse domain_crawler_metrics',
  'clickhouse-analytics': 'ClickHouse analytics',
};

const PARAMETER_KIND_LABELS: Record<QueryParameterResponse['kind'], string> = {
  fixed: 'Fixed',
  'run-time': 'Run-time',
};

const THRESHOLD_RULE_LABELS: Record<ThresholdRuleName, string> = {
  window: 'Window',
  'eligible-from': 'Eligible from',
  'current-window': 'Current window',
  baseline: 'Baseline',
  'baseline-floor': 'Baseline floor',
  'baseline-active-days': 'Baseline active days',
  'while-open-low-below': 'While open, low below',
  'recovered-below': 'Recovered below',
};

const purposeLabels = new Map<string, string>(Object.entries(PURPOSE_LABELS));
const sourceLabels = new Map<string, string>(Object.entries(SOURCE_LABELS));
const parameterKindLabels = new Map<string, string>(Object.entries(PARAMETER_KIND_LABELS));
const thresholdRuleLabels = new Map<string, string>(Object.entries(THRESHOLD_RULE_LABELS));

const PERCENT_PER_SHARE = 100;

const HUNDREDTHS = 100;

function asPercent(share: number): string {
  return `${Math.round(share * PERCENT_PER_SHARE * HUNDREDTHS) / HUNDREDTHS}%`;
}

export function queryOriginLabel(purpose: QueryPurpose, source: QuerySource): string {
  return `${purposeLabels.get(purpose) ?? purpose} · ${sourceLabels.get(source) ?? source}`;
}

export function parameterKindLabel(kind: QueryParameterResponse['kind']): string {
  return parameterKindLabels.get(kind) ?? kind;
}

function formatThresholdValue(value: number, unit: RuleUnit): string {
  if (unit === 'share') return asPercent(value);

  return `${value} ${unit}`;
}

export function ruleLine(rule: DetectorRuleResponse): string {
  if (rule.kind === 'severity') {
    const opens = rule.opensIncident ? ' (opens an incident)' : '';

    return `${severityLabel(rule.severity)}${opens} at or above: ${asPercent(rule.share)}`;
  }

  return `${thresholdRuleLabels.get(rule.name) ?? rule.name}: ${formatThresholdValue(rule.value, rule.unit)}`;
}

function indentationOf(line: string): number {
  return line.length - line.trimStart().length;
}

function withoutMargin(line: string, margin: number): string {
  if (line.trim() === '') return '';

  return line.slice(Math.min(margin, indentationOf(line)));
}

export function unindentedSql(sql: string): string {
  const lines = sql
    .replace(/^\s*\n/, '')
    .trimEnd()
    .split('\n');
  const margin = indentationOf(lines[0]);

  return lines.map((line) => withoutMargin(line, margin)).join('\n');
}

export function formatFixedValue(value: FixedParameterResponse['value']): string {
  return Array.isArray(value) ? value.join(', ') : String(value);
}
